/**
 * Puzzle 4 — The Hoodie Papoose (Apex).
 *
 * A jigsaw board is hidden until Minna activates AuraSight. While the
 * overlay is on, the target pattern glows. Basil walks up to the board
 * and uses the `,` interact key to cycle each slot's piece through the
 * palette. When every slot matches its expected piece the board emits
 * `JIGSAW_BOARD_SOLVED`.
 *
 * Both cats are required: the slots only take Basil's input while Minna's
 * Aura Sight is revealing the board (the prompt: "keep it on while you
 * switch to Basil"). `JigsawBoard.cycleSlot` walks each slot toward its
 * expected piece by itself, so without that gate Basil could solve the
 * apex alone by pressing `,` 21 times
 * (tools/playtest/scenarios/solo-solve.mjs).
 *
 * Unlike the other puzzles in this level, this one plugs into the scene
 * shell's `onAbilityKey` router to intercept `,` (Basil interact) —
 * ability keys for this puzzle are scoped to proximity to the board.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

import { JigsawBoard } from "../../../entities/interactives/JigsawBoard";

import { GameEvents } from "../../../config/events";
import { SCRAPYARD } from "../../../config/GameConfig";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { CueDef } from "../../../ui/tutorialCues";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

const CUE = {
  reveal: { cat: "minna", parts: [{ action: "aura" }, { text: "reveal the board" }] },
  match: { cat: "basil", parts: [{ action: "interact" }, { text: "turn each piece" }] },
} satisfies Record<string, CueDef>;
const OBJECTIVE = "Solve the hoodie jigsaw";
// Above the complete carrier display, so the chip doesn't cover its fabric.
const BOARD_ANCHOR = { x: 118, y: 7.7, z: 0 };

export class HoodieJigsawPuzzle implements PuzzleModule {
  readonly id = "hoodieJigsaw";
  readonly displayName = "The Hoodie Papoose";

  private ctx!: PuzzleContext;

  private jigsawBoard!: JigsawBoard;
  private solved = false;

  // Throttle for the "try Aura Sight here" discoverability hint so it
  // fires once per approach instead of every frame the player lingers
  // in the dead zone with the wrong ability set.
  private auraHintShownAt = -Infinity;

  // One-shot hint when Basil works the board while it is still hidden.
  private hiddenBoardHintShown = false;

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    // ── Jigsaw board (hidden until AuraSight reveals it) ──
    // Position was authored at x=120 but plat_s4_2 ends at x=119.75.
    // The board overhung the platform's east edge by 0.25u, so a real
    // player approaching from the east edge of plat_s4_2 could walk
    // OFF the platform onto the (visually-attached but physically-
    // unsupported) board. Surfaced in 2026-04-18 visual review +
    // PER-PUZZLE-STATUS real-bugs entry. Re-anchor at x=118 so the
    // board sits well inside the platform footprint.
    this.jigsawBoard = new JigsawBoard(scene, {
      key: "hoodieJigsaw",
      position: new Vector3(118, 5, 0),
      hidden: true,
      label: "Hoodie Pattern",
      numPieces: SCRAPYARD.PUZZLE.JIGSAW_PIECES,
      visualDepthOffset: -0.9,
      garmentStation: true,
    });

    ctx.registerInteractive({
      mesh: this.jigsawBoard.rootMesh,
      hidden: true,
      state: "hidden",
      onReveal: () => this.jigsawBoard.reveal(),
      onConceal: () => this.jigsawBoard.conceal(),
    });

    // ── Solved event ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.JIGSAW_BOARD_SOLVED, () => {
        if (this.solved) return;
        this.solved = true;
        this.jigsawBoard.retireCarrierDisplay();
        ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });

        // Canon p.13: the jigsaw's reward is the papoose itself — Minna
        // now wears the hoodie-turned-papoose and can carry calmed
        // kittens across the dangerous biking trail. The carrier
        // entity is owned by the scene shell so both this puzzle and
        // the Kitten Corral can share the same instance.
        if (ctx.papooseCarrier) {
          ctx.papooseCarrier.activate();
          ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
            message: "The hoodie becomes a papoose — Minna can carry calmed kittens now.",
          });
        }
      }),
    );
  }

  update(_dt: number, elapsed: number): void {
    // The AuraSight-driven pattern is visible only while the overlay is on.
    if (this.ctx.auraSight.isActive) {
      this.jigsawBoard.showPattern();
    } else {
      this.jigsawBoard.hidePattern();
    }

    // Discoverability hint: if the player wanders into the apex puzzle
    // zone without Aura Sight active, the entire puzzle is invisible —
    // the L1 QA pass flagged this as a "dead zone". Surface a one-shot
    // toast suggesting they try Q. Throttle on real elapsed time so a
    // player who toggles in/out of the zone doesn't re-trigger constantly.
    if (
      !this.solved &&
      !this.ctx.auraSight.isActive &&
      elapsed - this.auraHintShownAt > 12
    ) {
      const minnaDist = Vector3.Distance(
        this.ctx.minna.mesh.position,
        this.jigsawBoard.rootMesh.position,
      );
      const basilDist = Vector3.Distance(
        this.ctx.basil.mesh.position,
        this.jigsawBoard.rootMesh.position,
      );
      if (minnaDist < 6 || basilDist < 6) {
        this.auraHintShownAt = elapsed;
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Something feels hidden here. Try Q (Aura Sight) as Minna to reveal it.",
        });
      }
    }
  }

  /**
   * Cycle the slot nearest Basil — used by the scene shell's `,` keybind.
   * Returns true if Basil was close enough to the board (the key is then
   * consumed even when the hidden board can't take the input).
   */
  tryCycleNearestSlot(): boolean {
    const basilPos = this.ctx.basil.mesh.position;
    const dist = Vector3.Distance(basilPos, this.jigsawBoard.rootMesh.position);
    if (dist >= 3) return false;
    // Two-cat gate (see header): the board is concealed again as soon as
    // Minna's Aura Sight stops revealing it.
    if (this.jigsawBoard.state === "hidden") {
      if (!this.hiddenBoardHintShown) {
        this.hiddenBoardHintShown = true;
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Basil can't find the slots on a hidden board. Minna has to keep Aura Sight (Q) on it while he works.",
        });
      }
      return true;
    }
    this.jigsawBoard.cycleSlot(basilPos);
    return true;
  }

  /** Hide the pattern overlay — used when Minna toggles AuraSight off. */
  hidePattern(): void {
    this.jigsawBoard.hidePattern();
  }

  getCue(): PuzzleCue | null {
    if (this.solved) return null;
    // The slots only answer Basil while Minna's Aura Sight is on.
    const cue = this.ctx.auraSight.isActive ? CUE.match : CUE.reveal;
    return { objective: OBJECTIVE, cue, anchor: BOARD_ANCHOR };
  }

  getState(): PuzzleState {
    return {
      solved: this.solved,
      progress: this.solved ? 1 : 0,
    };
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        // The board mesh sits at x=120 but plat_s4_2's east edge is
        // at x=119.75 — the physics board-interact radius reaches back
        // across the platform, so target the platform-safe X at 118.5
        // (clearly on plat_s4_2 top 116.25..119.75) and let the
        // board's interact volume pick up the cat.
        { id: "jigsawBoard", position: { x: 118, y: 5 }, radius: 2.0, allowedCat: "either", label: "Hoodie jigsaw board" },
        { id: "p4_1_top",    position: { x: 112, y: 2.2 }, radius: 1.0, allowedCat: "either", label: "p4_1 top (apex spawn)" },
        { id: "p4_2_top",    position: { x: 118, y: 4.2 }, radius: 1.0, allowedCat: "either", label: "p4_2 top (board-adjacent)" },
      ],
      traversals: [
        // Apex section-4 hop: p4_1 (top y=2.2, x=[110, 114]) up to
        // p4_2 (top y=4.2, x=[116.25, 119.75]). Gap 2.25u east, height
        // +2.0u — running-jump territory. The jigsaw board sits on
        // plat_s4_2's east end so basil needs to clear the gap to
        // reach interact range.
        {
          id: "running_jump_p4_1_to_p4_2",
          kind: "jump",
          cat: "either",
          from: "p4_1_top",
          to: "p4_2_top",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 600 },
            { keyDown: " " },
            { waitMs: 300 }, // full-height jump: +2.0u rise
            { keyUp: " " },
            { waitMs: 700 },
            { keyUp: "d" },
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   { solved?: boolean }  // fire JIGSAW_BOARD_SOLVED + grant papoose
   *
   * The jigsaw is binary-state (slot-cycling is fast; the hard part is
   * discoverability / Aura Sight toggle), so the only useful seed is a
   * full solve — it hands Minna the papoose so the downstream kitten-
   * corral carry chain becomes testable without playing through the grid.
   */
  /** Checkpoint restore must replay the earned garment, not only progress. */
  onForceSolve(): void {
    this.seed({ solved: true });
  }

  seed(state: Record<string, unknown>): void {
    if (state.solved === true && !this.solved) {
      // Emit JIGSAW_BOARD_SOLVED so our own handler flips `solved`, grants
      // the papoose, and fires PUZZLE_SOLVED — single code path for both
      // real-play and seeded-play.
      this.ctx.gameEvents.emit(
        GameEvents.JIGSAW_BOARD_SOLVED,
        "hoodieJigsaw",
      );
    }
  }

  dispose(): void {
    this.jigsawBoard.dispose();
  }
}
